import { format, isWeekend } from "date-fns";
import { Plus, Trash2, Lock } from "lucide-react";
import { fmtDayMonth, fmtWeekday, fmtTime } from "../../lib/dates";

/**
 * The standard staff x date calendar grid — drag/drop reassignment, leave badges,
 * operation overlays, per-employee + daily totals. Extracted from RosterDetail.jsx.
 */
const RosterGrid = ({
  visibleDates,
  employees,
  roster,
  dragData,
  onDragStart,
  onDragOver,
  onDrop,
  onDeleteEntry,
  onAddEntryClick,
  getEntriesForDate,
  getShift,
  getInitials,
  countLeaveDaysForEmployee,
  getLeaveForCell,
  leaveBadge,
  operationForSlot,
}) => {
  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="roster-calendar">
      <div className="overflow-x-auto">
        <div style={{ minWidth: `${140 + visibleDates.length * 100 + 80}px` }}>
          {/* Calendar Header */}
          <div className="flex border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50 sticky top-0 z-10">
            <div className="w-[140px] min-w-[140px] p-2 border-r border-zinc-200 dark:border-zinc-800 sticky left-0 bg-zinc-50 dark:bg-zinc-800/50 z-20">
              <span className="text-[10px] uppercase tracking-[0.15em] font-medium text-zinc-500 dark:text-zinc-400">
                Staff
              </span>
            </div>
            {visibleDates.map((date, idx) => (
              <div
                key={idx}
                className={`w-[100px] min-w-[100px] p-2 text-center border-r border-zinc-200 dark:border-zinc-800 last:border-r-0 ${isWeekend(date) ? 'bg-zinc-100 dark:bg-zinc-800' : ''}`}
              >
                <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  {fmtWeekday(date)}
                </p>
                <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100 mt-0.5">
                  {fmtDayMonth(date)}
                </p>
              </div>
            ))}
            {/* Right Total column */}
            <div className="w-[80px] min-w-[80px] p-2 text-center bg-zinc-100 dark:bg-zinc-800 sticky right-0 z-20" data-testid="total-header-col">
              <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Total</p>
              <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100 mt-0.5">Shifts</p>
            </div>
          </div>

          {/* Calendar Body */}
          {employees.map((employee) => {
            const shiftCount = (roster.entries || []).filter(e => e.employee_id === employee.employee_id).length;
            const leaveCount = countLeaveDaysForEmployee(employee.employee_id);
            const empTotal = shiftCount + leaveCount;
            return (
            <div key={employee.employee_id} className="flex border-b border-zinc-200 dark:border-zinc-800 last:border-b-0">
              {/* Sticky employee name */}
              <div className="w-[140px] min-w-[140px] p-2 border-r border-zinc-200 dark:border-zinc-800 flex items-center gap-1.5 sticky left-0 bg-white dark:bg-zinc-900 z-10">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: '#52525B' }}
                >
                  {getInitials(employee.name)}
                </div>
                <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100 truncate leading-tight">
                  {employee.name}
                </span>
              </div>
              {/* Day cells */}
              {visibleDates.map((date, idx) => {
                const cellEntries = getEntriesForDate(date).filter(
                  e => e.employee_id === employee.employee_id
                );
                const dateStr = format(date, "yyyy-MM-dd");
                return (
                  <div
                    key={idx}
                    className={`w-[100px] min-w-[100px] p-1 min-h-[52px] border-r border-zinc-200 dark:border-zinc-800 last:border-r-0 ${isWeekend(date) ? 'bg-zinc-50 dark:bg-zinc-800/50' : ''} ${dragData ? 'hover:bg-blue-50' : ''} transition-colors relative group`}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, employee.employee_id, date)}
                  >
                    {cellEntries.map((entry, entryIdx) => {
                      const shift = getShift(entry.shift_id);
                      const op = operationForSlot(dateStr, entry.shift_id);
                      const opTitle = op ? `${op.name} (${op.is_hard ? "Hard" : "Soft"} op)` : "";
                      return (
                        <div
                          key={entryIdx}
                          draggable
                          onDragStart={(e) => onDragStart(e, entry)}
                          className="text-[10px] leading-tight px-1.5 py-1 rounded mb-0.5 cursor-grab active:cursor-grabbing relative group/entry"
                          style={op ? {
                            backgroundColor: op.color + '33',
                            borderLeft: `3px solid ${op.color}`,
                            boxShadow: `inset 0 0 0 1px ${op.color}55`,
                          } : {
                            backgroundColor: shift?.color + '20',
                            borderLeft: `2px solid ${shift?.color || '#002FA7'}`
                          }}
                          title={opTitle}
                          data-testid={`entry-${entry.entry_id}`}
                          data-op-id={op?.operation_id || ""}
                        >
                          {op && (
                            <span
                              className="absolute top-0 right-0 text-[7px] font-bold uppercase tracking-wide px-1 rounded-bl"
                              style={{ backgroundColor: op.color, color: "white" }}
                              data-testid={`op-badge-${entry.entry_id}`}
                            >
                              OP
                            </span>
                          )}
                          <div className="flex items-start justify-between gap-0.5">
                            <div>
                              <span className="font-semibold block flex items-center gap-1">
                                {entry.is_locked && (
                                  <Lock className="w-2.5 h-2.5 text-zinc-700 dark:text-zinc-300" data-testid={`locked-${entry.entry_id}`} title="Manually edited — preserved on regenerate" />
                                )}
                                {shift?.name || 'Shift'}
                              </span>
                              <span className="text-zinc-500 dark:text-zinc-400 tabular-nums">
                                {fmtTime(shift?.start_time)}-{fmtTime(shift?.end_time)}
                              </span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); onDeleteEntry(entry.entry_id); }}
                              className="opacity-0 group-hover/entry:opacity-100 text-red-400 hover:text-red-600 transition-opacity mt-0.5"
                              data-testid={`delete-entry-${entry.entry_id}`}
                            >
                              <Trash2 className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {cellEntries.length === 0 && (() => {
                      const leave = getLeaveForCell(employee.employee_id, dateStr);
                      if (leave) {
                        const b = leaveBadge(leave.leave_type);
                        return (
                          <div
                            className="text-[10px] leading-tight px-1.5 py-1 rounded mb-0.5 flex items-center justify-center font-bold tracking-wider"
                            style={{ backgroundColor: b.bg + '30', color: b.bg, border: `1px dashed ${b.bg}` }}
                            data-testid={`leave-cell-${employee.employee_id}-${dateStr}`}
                            title={`${leave.leave_type || "annual"} leave — ${leave.reason || "approved"}`}
                          >
                            {b.label}
                          </div>
                        );
                      }
                      return (
                        <button
                          onClick={() => onAddEntryClick({ date: dateStr, employee_id: employee.employee_id })}
                          className="w-full h-full min-h-[36px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`add-entry-${employee.employee_id}-${dateStr}`}
                        >
                          <Plus className="w-3 h-3 text-zinc-300" />
                        </button>
                      );
                    })()}
                  </div>
                );
              })}
              {/* Right total cell */}
              <div
                className="w-[80px] min-w-[80px] p-2 flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-800/50 sticky right-0 z-10"
                data-testid={`emp-total-${employee.employee_id}`}
              >
                <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100" title={`${shiftCount} rostered + ${leaveCount} leave credited = ${empTotal}`}>
                  {empTotal}
                </span>
                {leaveCount > 0 && (
                  <span
                    className="text-[9px] px-1 py-0.5 rounded bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300 tabular-nums font-medium"
                    title={`${shiftCount} rostered + ${leaveCount} leave credited = ${empTotal}`}
                    data-testid={`emp-total-breakdown-${employee.employee_id}`}
                  >
                    {shiftCount}+{leaveCount} lv
                  </span>
                )}
              </div>
            </div>
            );
          })}

          {/* Totals row */}
          <div className="flex border-t-2 border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/70" data-testid="totals-row">
            <div className="w-[140px] min-w-[140px] p-2 border-r border-zinc-200 dark:border-zinc-800 sticky left-0 bg-zinc-100 dark:bg-zinc-800 z-10 flex items-center">
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-700 dark:text-zinc-300">
                Daily Total
              </span>
            </div>
            {visibleDates.map((date, idx) => {
              const dayTotal = getEntriesForDate(date).length;
              return (
                <div
                  key={idx}
                  className={`w-[100px] min-w-[100px] p-2 text-center border-r border-zinc-200 dark:border-zinc-800 last:border-r-0 ${isWeekend(date) ? 'bg-zinc-100 dark:bg-zinc-800' : ''}`}
                  data-testid={`daily-total-${format(date, 'yyyy-MM-dd')}`}
                >
                  <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
                    {dayTotal}
                  </span>
                </div>
              );
            })}
            <div className="w-[80px] min-w-[80px] p-2 text-center bg-zinc-100 dark:bg-zinc-800 sticky right-0 z-10" data-testid="grand-total">
              <span className="text-sm font-bold tabular-nums text-[#002FA7] dark:text-blue-400">
                {roster.entries?.length || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RosterGrid;
